import { defineStore } from 'pinia';
import { useHubUiStore } from '@/stores/hubUi';

/** Payload for showNotificationNative */
interface NativeNotificationPayload {
  /** Notification title shown by the browser */
  title: string;
  /** Notification body text */
  body: string;
}

interface NotificationNativeState {
  /** Unused placeholder list; native notifications are fire-and-forget */
  notifications: any[];
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/notificationNative.ts` (namespace
 * `notificationNative`). In-memory only — the legacy module had no `vuexPersist`/reducer entry.
 *
 * No name collisions: legacy `getters` and `mutations` were both empty objects, so both legacy
 * actions convert 1:1 with their original names.
 *
 * Root-level access: legacy `commit('addSnackbarMessage', ..., { root: true })` →
 * `useHubUiStore().addAlert(...)` (the actually-rendered snackbar mechanism; `appStore`'s
 * `globalSnackbar`/`addSnackbarMessage` are never rendered anywhere), called inside the action
 * body (not at module top level) per the cross-store access rule.
 */
export const useNotificationNativeStore = defineStore('notificationNative', {
  state: (): NotificationNativeState => ({
    notifications: [],
  }),
  getters: {},
  actions: {
    async initNotificationNative(): Promise<NotificationPermission> {
      const status = Notification?.permission;

      if (status === 'granted' || status === 'denied') {
        return status;
      }

      const permission = await Notification.requestPermission();
      return permission;
    },
    // @notification: {title: String, body: String }
    async showNotificationNative(notification: NativeNotificationPayload) {
      if (!notification) return false;

      const status = await this.initNotificationNative();

      if (status !== 'granted') {
        useHubUiStore().addAlert({
          message: 'Notification permission denied. Please enable it in your browser settings.',
          type: 'error',
          timeout: 10000,
        });

        return false;
      }

      const _notification = new Notification(notification.title, {
        body: notification.body,
        icon: '/favicon.png',
      });

      _notification.onclick = function (event) {
        event.preventDefault(); // prevent the browser from focusing the Notification's tab
        if (window.location.pathname !== '/livechat') {
          window.focus();
          window.location.href = '/livechat';
        } else {
          window.focus();
        }
      };

      _notification.onerror = function (event: any) {
        const errorMessage = event?.message || 'An error occurred while showing the notification.';
        useHubUiStore().addAlert({ message: errorMessage, type: 'error', timeout: 10000 });
      };
    },
  },
});
